import { useRecoilValue } from 'recoil';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import ProjectPageContainer from 'src/components/ProjectPageContainer';
import { projectState } from 'src/states/project';
import { Modality } from 'src/types/api/data/image';

import ConfigureProjectButton from './ManageProject/ConfigureProjectButton';
import ConfirmProjectButton from './ManageProject/ConfirmProjectButton';
import ExportAnnotationsButton from './ManageProject/ExportAnnotationsButton';
import UploadAnnotationsButton from './ManageProject/UploadAnnotationsButton';

const ContentContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const TopCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: '14px',
  padding: '17px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '66px',
  flexWrap: 'wrap',
  gap: '16px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const MessageText = styled(Typography)`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.15px;
  color: #4a5565;
`;

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    flexDirection: 'column',
  },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: '12px',
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const StatCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: '14px',
  padding: '13px',
  display: 'flex',
  flexDirection: 'column',
  gap: '28px',
  minHeight: '98px',
}));

const StatLabel = styled(Typography)`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #6a7282;
`;

const StatValue = styled(Typography)`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: -0.44px;
  color: #0a0a0a;
`;

interface StatCardData {
  label: string;
  value: string | number;
}

function ProjectOverviewContent({
  modalityLabel,
  projectId,
}: {
  modalityLabel: Modality;
  projectId: string;
}): JSX.Element {
  const project = useRecoilValue(
    projectState.project({ projectId, modality: modalityLabel })
  );

  const stats: StatCardData[] = [
    {
      label: 'Annotation Job Progress',
      value: `${project.jobStats.annotation.completedStage} / ${project.jobStats.annotation.total}`,
    },
    {
      label: 'Available Annotation Jobs',
      value: project.jobStats.annotation.createdStage,
    },
    {
      label: 'Cross Annotation Job Progress',
      value: `${project.jobStats.cross.completedStage} / ${project.jobStats.cross.total}`,
    },
    {
      label: 'Available Cross Annotation Jobs',
      value: project.jobStats.cross.createdStage,
    },
    {
      label: 'Completed Review',
      value: project.jobStats.cross.completedStage,
    },
  ];

  const message = project.confirmed
    ? 'This project was already confirmed. Further updates not allowed.'
    : project.claim.assignRule?.corroboration?.finishWhenAnnotationDone
    ? 'This project will be finished when annotation jobs are done.'
    : 'This project has only corroboration jobs.';

  return (
    <ContentContainer>
      <TopCard>
        <MessageText>{message}</MessageText>
        <ButtonContainer>
          <ConfigureProjectButton
            projectId={projectId}
            modalityLabel={modalityLabel}
          />
          <ConfirmProjectButton
            projectId={projectId}
            modalityLabel={modalityLabel}
          />
          <ExportAnnotationsButton
            projectId={projectId}
            modalityLabel={modalityLabel}
          />
          <UploadAnnotationsButton
            projectId={projectId}
            modalityLabel={modalityLabel}
          />
        </ButtonContainer>
      </TopCard>

      <StatsContainer>
        {stats.map((stat, index) => (
          <StatCard key={index}>
            <StatLabel>{stat.label}</StatLabel>
            <StatValue>{stat.value}</StatValue>
          </StatCard>
        ))}
      </StatsContainer>
    </ContentContainer>
  );
}

export default function ProjectOverview(): JSX.Element {
  return (
    <ProjectPageContainer>
      {(projectId, modalityLabel) => (
        <ProjectOverviewContent
          modalityLabel={modalityLabel}
          projectId={projectId}
        />
      )}
    </ProjectPageContainer>
  );
}
